/* =============================================================================
 * NMS Reviews wall runtime — brand fork of Spectrum's sai-snippet-d1odzamh.js.
 *
 * SSR (sections/custom-blt-nms-reviews.liquid) renders the summary + first
 * page from Shopify metafields. This runtime:
 *
 *   1. Hydrates state from the seed JSON blob emitted by the Liquid section.
 *   2. Filter pills (ALL / N-star) re-fetch via the Spectrum reviews SDK.
 *   3. "load more reviews" appends the next page.
 *   4. JS masonry (shortest-column) layout, relaid on breakpoint changes.
 *   5. Voting (helpful / not helpful) via the SDK, logged-in customers only.
 *   6. Media lightbox with review side-panel.
 *
 * Data still flows through the app proxy (window.Spectrum.reviews SDK); only
 * the markup/skin is brand-owned. The Spectrum variant-swap + impression
 * analytics from the original are intentionally dropped.
 * ============================================================================= */

;(() => {
  if (window.__blt_nmsrev_initialized__) return
  window.__blt_nmsrev_initialized__ = true

  const TAG = 'blt-nms-reviews'
  const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

  function noopTrack() {}

  function formatDate(iso) {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  function starsSvg(rating, size) {
    const sz = size || 13
    let html = ''
    for (let i = 1; i <= 5; i++) {
      const cls = i <= rating ? 'blt-nmsrev__star-sm blt-nmsrev__star-sm--full' : 'blt-nmsrev__star-sm blt-nmsrev__star-sm--empty'
      html += `<svg class="${cls}" width="${sz}" height="${sz}" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.27 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"/></svg>`
    }
    return html
  }

  function verifiedBadgeSvg() {
    return `<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>`
  }

  /* Card markup mirrors the SSR structure in custom-blt-nms-reviews.liquid:
     media, then bodywrap { stars, title, body, reply, votes, attr(author+date) }. */
  function buildCard(r) {
    const rating = Math.round(Number(r.rating) || 0)
    const title = escapeHtml(r.title || '')
    const body = escapeHtml(r.body || '')
    const author = escapeHtml(r.authorName || 'Anonymous')
    const date = formatDate(r.reviewedAt)
    const reply = r.reply
    const votes = r.voteCounts || {}
    const currentVote = r.currentCustomerVote || null

    let html = `<article class="blt-nmsrev__card" data-review-id="${escapeHtml(r.id || '')}" data-action="card-preview">`

    const media = Array.isArray(r.media) ? r.media.filter((m) => m.url) : []
    if (media.length > 0) {
      const first = media[0]
      const reviewAttrs = ` data-review-rating="${rating}" data-review-author="${author}" data-review-title="${title}" data-review-body="${body}" data-review-date="${escapeHtml(r.reviewedAt || '')}" data-review-verified="${escapeHtml(r.verificationStatus || '')}"`
      html += `<div class="blt-nmsrev__card-media">`
      const allUrls = JSON.stringify(media.map((m) => m.url))
      const firstType = first.mediaType || 'image'
      const heroInner =
        firstType === 'video'
          ? `<video src="${escapeHtml(first.url)}" preload="metadata" muted playsinline></video><svg class="blt-nmsrev__play-overlay" width="48" height="48" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>`
          : `<img src="${escapeHtml(first.url)}" alt="" loading="lazy" onerror="this.parentElement.style.display='none'">`
      html += `<button class="blt-nmsrev__card-media-hero" type="button" data-action="media-open" data-media-url="${escapeHtml(first.url)}" data-media-type="${firstType}" data-media-all='${escapeHtml(allUrls).replace(/'/g, '&#39;')}'${reviewAttrs} aria-label="${firstType === 'video' ? 'Play video' : 'View image'}">${heroInner}</button>`
      if (media.length > 1) {
        html += `<span class="blt-nmsrev__card-media-count"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><rect x="7" y="7" width="10" height="10" rx="1"/></svg>+${media.length - 1}</span>`
      }
      html += '</div>'
    }

    html += `<div class="blt-nmsrev__card-bodywrap">`
    html += `<div class="blt-nmsrev__card-stars" aria-label="${rating} out of 5 stars">${starsSvg(rating, 13)}</div>`
    if (title) html += `<h3 class="blt-nmsrev__card-title">${title}</h3>`
    if (body) html += `<p class="blt-nmsrev__card-body">${body}</p>`

    if (reply) {
      html += `<div class="blt-nmsrev__reply">`
      html += `<span class="blt-nmsrev__reply-label">${escapeHtml(reply.label || 'Store reply')}</span>`
      html += `<div class="blt-nmsrev__reply-body">${escapeHtml(reply.body || '')}</div>`
      html += '</div>'
    }

    const isLoggedIn = !!window.__spectrumAi?.customer
    if (isLoggedIn && r.id) {
      const helpfulCount = votes.helpful || 0
      const notHelpfulCount = votes.not_helpful || 0
      html += `<div class="blt-nmsrev__votes">`
      html += `<span class="blt-nmsrev__votes-label">Helpful?</span>`
      html += `<button class="blt-nmsrev__vote-btn${currentVote === 'helpful' ? ' blt-nmsrev__vote-btn--active' : ''}" type="button" data-action="vote" data-vote-type="helpful" data-review-id="${escapeHtml(r.id)}"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3H14z"/><path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"/></svg><span class="blt-nmsrev__vote-count">${helpfulCount}</span></button>`
      html += `<button class="blt-nmsrev__vote-btn${currentVote === 'not_helpful' ? ' blt-nmsrev__vote-btn--active' : ''}" type="button" data-action="vote" data-vote-type="not_helpful" data-review-id="${escapeHtml(r.id)}"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3H10z"/><path d="M17 2h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17"/></svg><span class="blt-nmsrev__vote-count">${notHelpfulCount}</span></button>`
      html += '</div>'
    }

    html += `<div class="blt-nmsrev__card-attr">`
    html += `<span class="blt-nmsrev__card-author">${author}</span>`
    if (date) html += `<time class="blt-nmsrev__card-date">${date}</time>`
    html += '</div>'

    html += '</div></article>'
    return html
  }

  function buildShimmerCard(hasMedia) {
    let html = '<div class="blt-nmsrev__shimmer-card">'
    if (hasMedia) html += '<div class="blt-nmsrev__shimmer-media"></div>'
    html += '<div class="blt-nmsrev__shimmer-body">'
    html += '<div class="blt-nmsrev__shimmer-line blt-nmsrev__shimmer-line--stars"></div>'
    html += '<div class="blt-nmsrev__shimmer-line blt-nmsrev__shimmer-line--short"></div>'
    html += '<div class="blt-nmsrev__shimmer-line blt-nmsrev__shimmer-line--long"></div>'
    html += '<div class="blt-nmsrev__shimmer-line blt-nmsrev__shimmer-line--medium"></div>'
    html += '</div></div>'
    return html
  }

  /* Renders exactly `count` placeholder cards, distributed across the masonry
     columns the way the real cards will land (empty columns keep their flex
     slot so card widths match the final layout). */
  function buildShimmer(count, cols) {
    const colCount = Math.max(1, cols || 1)
    const total = Math.max(1, count || 1)
    const base = Math.floor(total / colCount)
    const rest = total % colCount
    let html = '<div class="blt-nmsrev__shimmer">'
    let i = 0
    for (let c = 0; c < colCount; c++) {
      const inCol = base + (c < rest ? 1 : 0)
      html += '<div class="blt-nmsrev__shimmer-col">'
      for (let k = 0; k < inCol; k++) {
        html += buildShimmerCard(i % 2 === 0)
        i++
      }
      html += '</div>'
    }
    html += '</div>'
    return html
  }

  if (!customElements.get(TAG)) {
    class BltNmsReviews extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return
        this._initialized = true
        this._track = noopTrack
        this._page = 1
        this._sortBy = 'newest'
        this._ratingFilter = null
        this._withMedia = false
        this._inflight = false
        this._paginationType = this.getAttribute('data-pagination-type') || 'load_more'
        this._totalPages = 1

        /* PRODUCT-CENTRIC: the wall shows the cumulative pool across the NMS +
           Court lines, not this one colorway. data-review-handles is the SSR set
           of sibling product handles; the master is the store-wide getAllReviews
           feed filtered to those. */
        this._master = null
        this._masterPromise = null
        try {
          this._allowed = new Set(JSON.parse(this.getAttribute('data-review-handles') || '[]'))
        } catch (_) {
          this._allowed = new Set()
        }

        this._hydrateSeed()
        this._updateLoadMore(this._page, this._totalPages)
        this._initMasonry()
        this._initFilterPills()
        this._initLoadMore()
        this._initVoting()
        this._initMediaLightbox()
        this._initWriteReview()

        /* swap the SSR seed (this colorway only) for the cumulative pool; keeps
           the SSR cards visible until the pool resolves (no shimmer flash) */
        this._loadPage(1, this._sortBy, null, { initial: true })
      }

      disconnectedCallback() {
        if (this._ro) this._ro.disconnect()
        for (const mq of [this._mqTablet, this._mqDesktop]) {
          if (!mq) continue
          if (mq.removeEventListener) mq.removeEventListener('change', this._relayoutBound)
          else if (mq.removeListener) mq.removeListener(this._relayoutBound)
        }
        if (this._lightbox?.open) {
          this._lightbox.close()
          document.body.style.overflow = ''
        }
      }

      _hydrateSeed() {
        const scope = this.parentElement || document
        const seedEl = scope.querySelector('script[data-blt-nmsrev-seed]')
        if (!seedEl) return
        try {
          const seed = JSON.parse(seedEl.textContent)
          this._stats = seed.stats || {}
          this._perPage = seed.perPage || 10
          const totalCount = Number(this.getAttribute('data-total-count')) || 0
          this._totalPages = this._perPage > 0 ? Math.ceil(totalCount / this._perPage) : 1
        } catch (_) {
          /* seed parse failure — SSR state is sufficient */
        }
      }

      _getProductHandle() {
        return this.getAttribute('data-product-handle') || ''
      }

      /* Called by the colorway-swap module after it morphs this section: the
         morph keeps THIS element instance but replaces its inner DOM with the
         new colorway's server render — whose photos count and seed cards are
         per-product, not per-line. Re-scope and re-render from the cached pool
         (same line → same master). A fresh element instance ignores this and
         lets connectedCallback do the work. */
      _postSwapRefresh() {
        if (!this._initialized) return
        this._ratingFilter = null
        this._withMedia = false
        this._page = 1
        try {
          this._allowed = new Set(JSON.parse(this.getAttribute('data-review-handles') || '[]'))
        } catch (_) {
          this._allowed = new Set()
        }
        this._hydrateSeed()
        if (this._master) this._recomputePills()
        this._syncPills()
        this._loadPage(1, this._sortBy, null, { initial: true })
      }

      /* How many cards the pending fetch will return, from the SSR seed:
         a rating filter's count comes from the histogram, the photos filter from
         the pill's data-count, ALL from the total — all capped to one page.
         Falls back to 3 if the seed is missing. */
      _expectedCount(ratingFilter) {
        const total = Number(this.getAttribute('data-total-count')) || this._stats?.totalCount || 0
        const hist = this._stats?.histogram || {}
        let expected = total
        if (this._withMedia) {
          const pill = this.querySelector('[data-action="filter-photos"]')
          expected = pill ? Number(pill.getAttribute('data-count')) || 0 : 0
        } else if (ratingFilter) {
          expected = Number(hist[String(ratingFilter)]) || 0
        }
        return Math.max(1, Math.min(expected || 3, this._perPage || 10))
      }

      async _hydrateVoteCounts() {
        if (!window.__spectrumAi?.customer) return
        this._ensureProxyBase()
        const sdk = window.Spectrum?.reviews
        if (!sdk?.getReviews) return
        try {
          const result = await sdk.getReviews(this._getProductHandle(), {
            page: 1,
            limit: this._perPage || 10,
            sortBy: this._sortBy,
          })
          if (!result.ok) return
          const data = result.data?.data || result.data
          const reviews = data?.reviews || []
          for (const r of reviews) {
            if (!r.id || !r.voteCounts) continue
            const cards = this.querySelectorAll(
              `[data-review-id="${r.id}"], [data-review-id="spectrum-review-${r.id}"]`,
            )
            for (const card of cards) {
              const review = card.closest('.blt-nmsrev__card') || card
              const voteBtns = review.querySelectorAll('[data-action="vote"]')
              for (const vb of voteBtns) {
                const type = vb.getAttribute('data-vote-type')
                const count = r.voteCounts[type] || 0
                const countEl = vb.querySelector('.blt-nmsrev__vote-count')
                if (countEl) countEl.textContent = count
                vb.classList.toggle('blt-nmsrev__vote-btn--active', r.currentCustomerVote === type)
              }
            }
          }
        } catch (_) {
          /* best-effort hydration */
        }
      }

      _ensureProxyBase() {
        if (window.__spectrumAi?.baseProxyUrl) return
        if (!window.__spectrumAi) window.__spectrumAi = {}
        const configEl = document.querySelector('script[data-spectrum-config]')
        if (configEl) {
          try {
            const cfg = JSON.parse(configEl.textContent)
            if (cfg.baseProxyUrl) {
              window.__spectrumAi.baseProxyUrl = cfg.baseProxyUrl
              return
            }
          } catch (_) {
            /* fall through */
          }
        }
        const org = document.querySelector('[data-spectrum-org]')?.getAttribute('data-spectrum-org')
        window.__spectrumAi.baseProxyUrl = org ? `/apps/spectrum_ai_${org}` : '/apps/spectrum'
      }

      async _fetchReviews(page, sortBy, ratingFilter) {
        this._ensureProxyBase()
        const sdk = window.Spectrum?.reviews
        if (!sdk) throw new Error('Spectrum SDK not available')
        const opts = { page, limit: this._perPage, sortBy }
        if (ratingFilter) opts.rating = ratingFilter
        /* Spectrum's SDK passes any opt straight through as a query param;
           withMedia=true is the documented "reviews that have photos" filter. */
        if (this._withMedia) opts.withMedia = 'true'
        const result = await sdk.getReviews(this._getProductHandle(), opts)
        if (!result.ok) throw new Error(result.error?.message || 'Failed to fetch reviews')
        const body = result.data
        return body.data || body
      }

      /* Load the cumulative pool ONCE: page through the store-wide getAllReviews
         feed (limit ≤ 50), then keep only reviews whose originProductHandle is in
         this line's sibling set. Cached — filter/sort/load-more are then pure
         in-memory slicing (the pool is small, ~dozens). */
      /* the Spectrum SDK script loads async; poll briefly so the very first
         (on-connect) pool fetch doesn't lose a race with it */
      _waitForSdk(ms = 8000) {
        return new Promise((resolve, reject) => {
          const t0 = Date.now()
          const tick = () => {
            const sdk = window.Spectrum?.reviews
            if (sdk?.getAllReviews) return resolve(sdk)
            if (Date.now() - t0 > ms) return reject(new Error('getAllReviews unavailable'))
            setTimeout(tick, 120)
          }
          tick()
        })
      }

      _poolCacheKey() {
        const handles = [...(this._allowed || [])].sort().join(',')
        return 'bltnmsrev:pool:' + handles
      }
      _readPoolCache() {
        try {
          const raw = sessionStorage.getItem(this._poolCacheKey())
          const arr = raw && JSON.parse(raw)
          return Array.isArray(arr) && arr.length ? arr : null
        } catch (_) {
          return null
        }
      }
      _writePoolCache(master) {
        try {
          sessionStorage.setItem(this._poolCacheKey(), JSON.stringify(master))
        } catch (_) {
          /* quota/private mode — cache is best-effort */
        }
      }

      async _fetchPool() {
        this._ensureProxyBase()
        const sdk = await this._waitForSdk()
        const limit = 50
        let page = 1
        let totalPages = 1
        const collected = []
        do {
          const res = await sdk.getAllReviews({ page, limit, sortBy: 'newest' })
          if (!res.ok) throw new Error(res.error?.message || 'Failed to fetch reviews')
          const data = res.data?.data || res.data || {}
          collected.push(...(data.reviews || []))
          totalPages = data.pagination?.totalPages || 1
          page += 1
        } while (page <= totalPages && page <= 6) /* hard cap 300 */
        const allowed = this._allowed
        const src = allowed && allowed.size
          ? collected.filter((r) => allowed.has(r.originProductHandle))
          : collected
        const seen = new Set()
        const master = []
        for (const r of src) {
          if (r.id) {
            if (seen.has(r.id)) continue
            seen.add(r.id)
          }
          master.push(r)
        }
        return master
      }

      /* The per-product server seed differs from the line pool, so the wall
         visibly changed once the (slow, cold app-proxy) pool landed. The pool
         is session-cached per line: every load after the first renders the
         correct wall immediately, then revalidates in the background and only
         re-renders if the data actually changed. */
      async _ensureMaster() {
        if (this._master) return this._master
        if (this._masterPromise) return this._masterPromise
        const cached = this._readPoolCache()
        if (cached) {
          this._master = cached
          this._revalidatePool()
          return this._master
        }
        this._masterPromise = (async () => {
          const master = await this._fetchPool()
          this._master = master
          this._writePoolCache(master)
          return master
        })()
        /* don't let a failed attempt poison retries — clear the cached promise
           on rejection so a later interaction (or a slow SDK) can try again */
        this._masterPromise.catch(() => {
          this._masterPromise = null
        })
        return this._masterPromise
      }

      _revalidatePool() {
        if (this._revalidating) return
        this._revalidating = true
        this._fetchPool()
          .then((fresh) => {
            this._revalidating = false
            const changed = JSON.stringify(fresh) !== JSON.stringify(this._master)
            if (!changed) return
            this._master = fresh
            this._writePoolCache(fresh)
            this._recomputePills()
            /* refresh the visible list only when the shopper hasn't started
               filtering/paging — otherwise just the counts update */
            if (this._ratingFilter === null && !this._withMedia && this._page === 1) {
              this._loadPage(1, this._sortBy, null, { initial: true })
            }
          })
          .catch(() => {
            this._revalidating = false
          })
      }

      _hasMedia(r) {
        return Array.isArray(r.media) && r.media.some((m) => m && m.url)
      }

      _filteredSorted(ratingFilter, withMedia, sortBy) {
        let arr = this._master || []
        if (ratingFilter) arr = arr.filter((r) => Math.round(Number(r.rating) || 0) === Number(ratingFilter))
        if (withMedia) arr = arr.filter((r) => this._hasMedia(r))
        arr = arr.slice()
        const ts = (r) => {
          const t = Date.parse(r.reviewedAt || '')
          return Number.isNaN(t) ? 0 : t
        }
        if (sortBy === 'oldest') arr.sort((a, b) => ts(a) - ts(b))
        else if (sortBy === 'highest') arr.sort((a, b) => (b.rating || 0) - (a.rating || 0))
        else if (sortBy === 'lowest') arr.sort((a, b) => (a.rating || 0) - (b.rating || 0))
        else arr.sort((a, b) => ts(b) - ts(a))
        return arr
      }

      /* Rebuild the filter-pill counts from the actual pool (the SSR star pills
         are already cumulative from the summed histogram; this mainly corrects
         the WITH PHOTOS count, which SSR can't total across products). */
      _recomputePills() {
        const master = this._master || []
        const filters = this.querySelector('[data-slot="filters"]')
        if (!filters) return
        const hist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        let media = 0
        for (const r of master) {
          const s = Math.round(Number(r.rating) || 0)
          if (hist[s] != null) hist[s] += 1
          if (this._hasMedia(r)) media += 1
        }
        filters.querySelectorAll('[data-rating-filter]').forEach((p) => {
          const star = p.getAttribute('data-rating-filter')
          const c = hist[Number(star)] || 0
          if (c === 0) {
            p.hidden = true
          } else {
            p.hidden = false
            p.setAttribute('data-count', String(c))
            p.setAttribute('aria-label', `Filter by ${star} star reviews (${c})`)
            /* the count is DISPLAYED (matches the WITH PHOTOS pill), so keep the
               visible label in sync with the pool, not just the attribute */
            p.innerHTML = `${star} ★ &nbsp;&middot;&nbsp; ${c}`
          }
        })
        let photoBtn = filters.querySelector('[data-action="filter-photos"]')
        if (media > 0) {
          if (!photoBtn) {
            photoBtn = document.createElement('button')
            photoBtn.className = 'blt-nmsrev__pill'
            photoBtn.type = 'button'
            photoBtn.setAttribute('data-action', 'filter-photos')
            const allBtn = filters.querySelector('[data-action="filter-all"]')
            if (allBtn) allBtn.insertAdjacentElement('afterend', photoBtn)
            else filters.appendChild(photoBtn)
          }
          photoBtn.hidden = false
          photoBtn.setAttribute('data-count', String(media))
          photoBtn.setAttribute('aria-label', `Filter to reviews with photos (${media})`)
          photoBtn.innerHTML = `WITH PHOTOS &nbsp;&middot;&nbsp; ${media}`
        } else if (photoBtn) {
          photoBtn.hidden = true
        }
      }

      async _loadPage(page, sortBy, ratingFilter, opts) {
        const append = opts?.append === true
        const initial = opts?.initial === true

        const listEl = this.querySelector('[data-slot="reviews"]')
        if (!listEl) return

        /* first call loads the pool; show a shimmer only for an explicit
           (non-initial) fetch so the SSR seed never flickers on page load */
        if (!this._master) {
          if (this._inflight) return
          this._inflight = true
          const loadMoreBtn = this.querySelector('[data-action="load-more"]')
          if (!append && !initial) {
            listEl.innerHTML = buildShimmer(this._expectedCount(ratingFilter), this._getColCount())
            listEl.classList.remove('blt-nmsrev__grid--ssr')
          } else if (append && loadMoreBtn) {
            loadMoreBtn.disabled = true
            loadMoreBtn.textContent = 'loading…'
          }
          try {
            await this._ensureMaster()
          } catch (err) {
            this._inflight = false
            if (loadMoreBtn) {
              loadMoreBtn.disabled = false
              loadMoreBtn.textContent = 'load more reviews'
            }
            /* leave the SSR seed in place on failure (graceful) */
            if (!initial && !append && !listEl.querySelector('.blt-nmsrev__card')) {
              listEl.innerHTML = `<p class="blt-nmsrev__empty">failed to load reviews. please try again.</p>`
            }
            return
          }
          this._inflight = false
          this._recomputePills()
        }

        /* render synchronously from the cached pool */
        const all = this._filteredSorted(ratingFilter, this._withMedia, sortBy)
        this._page = page
        this._totalPages = Math.max(1, Math.ceil(all.length / this._perPage))
        const loadMoreBtn = this.querySelector('[data-action="load-more"]')

        /* the on-connect swap can land seconds after load, while the shopper is
           already at/below the wall — if the grid grows above their viewport the
           whole page (and the buttons under their cursor) lurches. Compensate
           the scroll by the height delta so what they're looking at stays put. */
        const gridTopBefore = listEl.getBoundingClientRect().top
        const gridHBefore = listEl.offsetHeight

        if (!append && initial && listEl.querySelector('.blt-nmsrev__card')) {
          /* first-visit seed → pool swap: fade rather than hard-cut */
          listEl.style.transition = 'opacity 0.25s ease'
          listEl.style.opacity = '0.25'
        }
        if (append) {
          const slice = all.slice((page - 1) * this._perPage, page * this._perPage)
          const tmp = document.createElement('div')
          tmp.innerHTML = slice.map((r) => buildCard(r)).join('')
          this._appendToMasonry(listEl, Array.from(tmp.children))
        } else if (all.length === 0) {
          const emptyText = this.getAttribute('data-empty-text') || 'no reviews yet.'
          const filtered = ratingFilter || this._withMedia
          listEl.innerHTML = `<p class="blt-nmsrev__empty">${filtered ? 'no reviews match this filter.' : emptyText}</p>`
        } else {
          const slice = all.slice(0, page * this._perPage)
          const tmp = document.createElement('div')
          tmp.innerHTML = slice.map((r) => buildCard(r)).join('')
          this._layoutMasonry(listEl, Array.from(tmp.children))
        }

        if (initial && gridTopBefore < 0) {
          const delta = listEl.offsetHeight - gridHBefore
          if (delta) window.scrollBy(0, delta)
        }
        if (listEl.style.opacity && listEl.style.opacity !== '1') {
          requestAnimationFrame(() => { listEl.style.opacity = '1' })
        }

        this._updateLoadMore(this._page, this._totalPages)
        if (loadMoreBtn) {
          loadMoreBtn.disabled = false
          loadMoreBtn.textContent = 'load more reviews'
        }
      }

      _updateLoadMore(page, totalPages) {
        const loadMoreEl = this.querySelector('[data-slot="load-more"]')
        if (!loadMoreEl) return
        loadMoreEl.hidden = page >= totalPages
      }

      /* "write a review" must open the Spectrum dialog EVERY time. The bare
         #write-review anchor relies on a hashchange — which never fires when
         the URL already carries that hash (it survives reloads), leaving the
         button permanently dead. Open the dialog host directly; fall back to
         forcing a real hashchange by clearing a stale hash first. */
      _initWriteReview() {
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-action="write-review"]')
          if (!btn) return
          e.preventDefault()
          const host = document.querySelector('sai-rvwdlg01')
          if (host && typeof host._openDialog === 'function') {
            host._userOpened = true
            host._openDialog('trigger')
            return
          }
          if ((window.location.hash || '') === '#write-review') {
            try {
              window.history.replaceState(null, '', window.location.pathname + window.location.search)
            } catch (_) {
              /* history unavailable */
            }
          }
          window.location.hash = 'write-review'
        })
      }

      _initLoadMore() {
        const loadMoreEl = this.querySelector('[data-slot="load-more"]')
        if (!loadMoreEl) return
        loadMoreEl.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-action="load-more"]')
          if (!btn || this._inflight) return
          this._loadPage(this._page + 1, this._sortBy, this._ratingFilter, { append: true })
        })
      }

      _getColCount() {
        if (matchMedia('(min-width: 990px)').matches) return 3
        if (matchMedia('(min-width: 768px)').matches) return 2
        return 1
      }

      _initMasonry() {
        const grid = this.querySelector('[data-slot="reviews"]')
        if (!grid) return
        const cards = Array.from(grid.querySelectorAll('.blt-nmsrev__card'))
        if (cards.length === 0) return
        grid.classList.remove('blt-nmsrev__grid--ssr')
        this._layoutMasonry(grid, cards)

        /* Relayout only when the column count actually changes, so a rotation,
           a resize, or a stale first measurement can never leave cards spread
           across more columns than the viewport can hold. */
        this._relayoutBound = () => {
          const g = this.querySelector('[data-slot="reviews"]')
          if (!g) return
          const want = this._getColCount()
          if (want === g.querySelectorAll('.blt-nmsrev__grid-col').length) return
          const allCards = Array.from(g.querySelectorAll('.blt-nmsrev__card'))
          if (allCards.length > 0) this._layoutMasonry(g, allCards)
        }

        if (typeof ResizeObserver !== 'undefined') {
          this._ro = new ResizeObserver(() => {
            if (this._roTick) return
            this._roTick = true
            requestAnimationFrame(() => {
              this._roTick = false
              this._relayoutBound()
            })
          })
          this._ro.observe(this)
        }

        /* matchMedia fallback for browsers without ResizeObserver; addListener
           is the pre-Safari-14 spelling and still needs supporting. */
        this._mqTablet = matchMedia('(min-width: 768px)')
        this._mqDesktop = matchMedia('(min-width: 990px)')
        for (const mq of [this._mqTablet, this._mqDesktop]) {
          if (mq.addEventListener) mq.addEventListener('change', this._relayoutBound)
          else if (mq.addListener) mq.addListener(this._relayoutBound)
        }
      }

      _layoutMasonry(grid, cards) {
        const colCount = this._getColCount()
        grid.innerHTML = ''
        grid.classList.remove('blt-nmsrev__grid--ssr')
        const cols = []
        for (let i = 0; i < colCount; i++) {
          const col = document.createElement('div')
          col.className = 'blt-nmsrev__grid-col'
          grid.appendChild(col)
          cols.push({ el: col, h: 0 })
        }
        for (const card of cards) {
          const shortest = cols.reduce((a, b) => (a.h <= b.h ? a : b))
          shortest.el.appendChild(card)
          shortest.h += card.offsetHeight || 100
        }
      }

      _appendToMasonry(grid, newCards) {
        const cols = Array.from(grid.querySelectorAll('.blt-nmsrev__grid-col'))
        if (cols.length === 0) {
          this._layoutMasonry(grid, newCards)
          return
        }
        const colData = cols.map((el) => ({ el, h: el.offsetHeight || 0 }))
        for (const card of newCards) {
          const shortest = colData.reduce((a, b) => (a.h <= b.h ? a : b))
          shortest.el.appendChild(card)
          shortest.h += card.offsetHeight || 100
        }
      }

      /* Figma filter pills: ALL + WITH PHOTOS + one pill per non-empty rating.
         Only one filter is active at a time — picking any pill clears the rest,
         and re-tapping the active pill (or ALL) returns to everything. */
      _initFilterPills() {
        const filters = this.querySelector('[data-slot="filters"]')
        if (!filters) return
        filters.addEventListener('click', (e) => {
          const allBtn = e.target.closest('[data-action="filter-all"]')
          const photoBtn = e.target.closest('[data-action="filter-photos"]')
          const row = e.target.closest('[data-rating-filter]')
          if (!allBtn && !photoBtn && !row) return

          if (allBtn) {
            if (this._ratingFilter === null && !this._withMedia) return
            this._ratingFilter = null
            this._withMedia = false
          } else if (photoBtn) {
            const next = !this._withMedia
            this._withMedia = next
            this._ratingFilter = null
          } else {
            const val = Number(row.getAttribute('data-rating-filter'))
            const next = this._ratingFilter === val ? null : val
            this._ratingFilter = next
            this._withMedia = false
          }

          this._syncPills()
          this._page = 1
          this._loadPage(1, this._sortBy, this._ratingFilter)
        })
        this._syncPills()
      }

      _syncPills() {
        const filters = this.querySelector('[data-slot="filters"]')
        if (!filters) return
        filters.querySelectorAll('[data-rating-filter]').forEach((p) => {
          p.classList.toggle('is-active', this._ratingFilter === Number(p.getAttribute('data-rating-filter')))
        })
        const photoBtn = filters.querySelector('[data-action="filter-photos"]')
        if (photoBtn) photoBtn.classList.toggle('is-active', this._withMedia)
        const allBtn = filters.querySelector('[data-action="filter-all"]')
        if (allBtn) allBtn.classList.toggle('is-active', this._ratingFilter === null && !this._withMedia)
      }

      _initMediaLightbox() {
        if (this._mediaBound) return
        this._mediaBound = true

        const dialog = document.createElement('dialog')
        dialog.className = 'blt-nmsrev__lightbox'
        dialog.setAttribute('aria-modal', 'true')
        dialog.setAttribute('aria-label', 'Media viewer')
        dialog.innerHTML = `
          <div class="blt-nmsrev__lightbox-panel">
            <button class="blt-nmsrev__lightbox-close" type="button" data-action="lightbox-close" aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" stroke-linecap="round"/></svg>
            </button>
            <div class="blt-nmsrev__lightbox-media-side">
              <span class="blt-nmsrev__lightbox-counter" data-slot="lightbox-counter"></span>
              <button class="blt-nmsrev__lightbox-nav blt-nmsrev__lightbox-nav--prev" type="button" data-action="lightbox-prev" aria-label="Previous" hidden>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10 3L5 8L10 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
              <div class="blt-nmsrev__lightbox-content"></div>
              <button class="blt-nmsrev__lightbox-nav blt-nmsrev__lightbox-nav--next" type="button" data-action="lightbox-next" aria-label="Next" hidden>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 3L11 8L6 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </div>
            <div class="blt-nmsrev__lightbox-review-side" data-slot="lightbox-review"></div>
          </div>
        `
        this.appendChild(dialog)
        this._lightbox = dialog
        this._lightboxItems = []
        this._lightboxIndex = 0

        const readItem = (t) => ({
          url: t.getAttribute('data-media-url'),
          type: t.getAttribute('data-media-type') || 'image',
          rating: Number(t.getAttribute('data-review-rating')) || 0,
          author: t.getAttribute('data-review-author') || '',
          title: t.getAttribute('data-review-title') || '',
          body: t.getAttribute('data-review-body') || '',
          date: t.getAttribute('data-review-date') || '',
          verified: t.getAttribute('data-review-verified') === 'verified_buyer',
        })

        const openLightbox = () => {
          this._showLightboxSlide()
          dialog.showModal()
          document.body.style.overflow = 'hidden'
        }

        this.addEventListener('click', (e) => {
          if (e.target.closest('[data-action="vote"]')) return
          if (e.target.closest('[data-action="write-review"]')) return
          if (e.target.closest('.blt-nmsrev__filters')) return
          if (e.target.closest('.blt-nmsrev__actions')) return

          const card = e.target.closest('[data-action="card-preview"]')
          if (!card) return

          e.preventDefault()
          e.stopPropagation()

          const hero = card.querySelector('[data-action="media-open"]')
          if (hero) {
            const allMediaAttr = hero.getAttribute('data-media-all')
            if (allMediaAttr) {
              try {
                const urls = JSON.parse(allMediaAttr)
                const base = readItem(hero)
                this._lightboxItems = urls.map((u) => {
                  const isVideo = u.match(/\.(mp4|webm|mov)(\?|$)/i)
                  return { ...base, url: u, type: isVideo ? 'video' : 'image' }
                })
              } catch (_) {
                this._lightboxItems = [readItem(hero)]
              }
            } else {
              this._lightboxItems = [readItem(hero)]
            }
          } else {
            this._lightboxItems = [
              {
                url: '',
                type: 'none',
                rating: card.querySelectorAll('.blt-nmsrev__star-sm--full').length,
                author: card.querySelector('.blt-nmsrev__card-author')?.textContent || '',
                title: card.querySelector('.blt-nmsrev__card-title')?.textContent || '',
                body: card.querySelector('.blt-nmsrev__card-body')?.textContent || '',
                date: card.querySelector('.blt-nmsrev__card-date')?.textContent || '',
                verified: false,
              },
            ]
          }
          this._lightboxIndex = 0
          openLightbox()
        })

        const closeLightbox = () => {
          dialog.classList.add('blt-nmsrev__lightbox--closing')
          setTimeout(() => {
            dialog.classList.remove('blt-nmsrev__lightbox--closing')
            dialog.close()
            document.body.style.overflow = ''
          }, 250)
        }

        dialog.addEventListener('cancel', (e) => {
          e.preventDefault()
          closeLightbox()
        })

        dialog.addEventListener('click', (e) => {
          const action = e.target.closest('[data-action]')?.getAttribute('data-action')
          if (action === 'lightbox-close') {
            closeLightbox()
            return
          }
          if (action === 'lightbox-prev') {
            this._lightboxIndex = Math.max(0, this._lightboxIndex - 1)
            this._showLightboxSlide()
            return
          }
          if (action === 'lightbox-next') {
            this._lightboxIndex = Math.min(this._lightboxItems.length - 1, this._lightboxIndex + 1)
            this._showLightboxSlide()
            return
          }
          if (e.target === dialog) closeLightbox()
        })
      }

      _showLightboxSlide() {
        const item = this._lightboxItems[this._lightboxIndex]
        if (!item) return
        const mediaSide = this._lightbox.querySelector('.blt-nmsrev__lightbox-media-side')
        const content = this._lightbox.querySelector('.blt-nmsrev__lightbox-content')
        if (item.type === 'none' || !item.url) {
          if (mediaSide) mediaSide.hidden = true
          content.innerHTML = ''
        } else {
          if (mediaSide) mediaSide.hidden = false
          if (item.type === 'video') {
            content.innerHTML = `<video class="blt-nmsrev__lightbox-media" src="${escapeHtml(item.url)}" controls autoplay playsinline></video>`
          } else {
            content.innerHTML = `<img class="blt-nmsrev__lightbox-media" src="${escapeHtml(item.url)}" alt="" onerror="this.style.display='none'">`
          }
        }

        const reviewSide = this._lightbox.querySelector('[data-slot="lightbox-review"]')
        if (reviewSide && (item.author || item.title || item.body)) {
          const rating = Math.round(item.rating || 0)
          const date = formatDate(item.date)
          let html = `<div class="blt-nmsrev__lightbox-review-meta">`
          html += `<span class="blt-nmsrev__card-author">${escapeHtml(item.author)}</span>`
          if (item.verified)
            html += `<span class="blt-nmsrev__badge">${verifiedBadgeSvg()}<span>Verified</span></span>`
          html += '</div>'
          if (date) html += `<time class="blt-nmsrev__lightbox-review-date">${date}</time>`
          html += `<div class="blt-nmsrev__lightbox-review-stars">${starsSvg(rating, 13)}</div>`
          if (item.title)
            html += `<h3 class="blt-nmsrev__lightbox-review-title">${escapeHtml(item.title)}</h3>`
          if (item.body)
            html += `<div class="blt-nmsrev__lightbox-review-body">${escapeHtml(item.body)}</div>`
          reviewSide.innerHTML = html
          reviewSide.hidden = false
        } else if (reviewSide) {
          reviewSide.hidden = true
        }

        const counter = this._lightbox.querySelector('[data-slot="lightbox-counter"]')
        if (counter) counter.textContent = `${this._lightboxIndex + 1}/${this._lightboxItems.length}`

        const prev = this._lightbox.querySelector('[data-action="lightbox-prev"]')
        const next = this._lightbox.querySelector('[data-action="lightbox-next"]')
        if (prev) prev.hidden = this._lightboxIndex <= 0
        if (next) next.hidden = this._lightboxIndex >= this._lightboxItems.length - 1
      }

      _initVoting() {
        if (this._voteBound) return
        this._voteBound = true
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-action="vote"]')
          if (!btn) return
          let reviewId = btn.getAttribute('data-review-id')
          if (!reviewId) return
          const voteType = btn.getAttribute('data-vote-type')
          if (!voteType) return
          // SSR renders metaobject handle (spectrum-review-{uuid}); extract UUID
          if (reviewId.startsWith('spectrum-review-')) {
            reviewId = reviewId.replace('spectrum-review-', '')
          }
          this._vote(reviewId, voteType, btn)
        })
      }

      async _vote(reviewId, voteType, btn) {
        this._ensureProxyBase()
        const sdk = window.Spectrum?.reviews
        if (!sdk) return
        btn.disabled = true
        btn.style.opacity = '0.5'
        try {
          const result = await sdk.voteOnReview(reviewId, voteType, {
            productHandle: this._getProductHandle(),
          })
          btn.disabled = false
          btn.style.opacity = ''
          if (!result.ok) return
          const voteData = result.data?.data || result.data

          const card = btn.closest('.blt-nmsrev__card')
          if (!card) return

          const voteBtns = card.querySelectorAll('[data-action="vote"]')
          for (const vb of voteBtns) {
            const type = vb.getAttribute('data-vote-type')
            const count = voteData?.voteCounts?.[type] || 0
            vb.classList.toggle(
              'blt-nmsrev__vote-btn--active',
              voteData?.action !== 'removed' && type === voteType,
            )
            const countEl = vb.querySelector('.blt-nmsrev__vote-count')
            if (countEl) countEl.textContent = count
          }
        } catch (_) {
          btn.disabled = false
          btn.style.opacity = ''
          /* vote failure is silent */
        }
      }
    }
    customElements.define(TAG, BltNmsReviews)
  }
})()
